<script lang="ts">
    import type { Selection } from '@cropper/element-selection';
    import {
        VpArrowUturnForwardIcon,
        VpCheckIcon,
        VpCloseIcon,
        VpRefreshIcon,
    } from '@decathlon-experience/icons/svelte';
    import Cropper, {
        type CropperCanvas,
        type CropperHandle,
        type CropperImage,
        type CropperSelection,
    } from 'cropperjs';
    import { createEventDispatcher, onDestroy, onMount } from 'svelte';
    import { writable } from 'svelte/store';

    import { trans } from '$assets/translator';
    import {
        MIN_HEIGHT,
        MIN_WIDTH,
    } from '$backoffice/views/MediaManager/utils';
    import Button from '$saga/components/Button.svelte';
    import Modal from '$saga/components/Modal.svelte';

    export let file: File | null = null;
    export let src: string | null = null;
    export let cropProxyUrl: string;

    const dispatch = createEventDispatcher<{
        cropped: { file: File };
        cancel: void;
    }>();

    // Modal open state — always open while this component is mounted
    const isOpen = writable(true);

    let croppedFile: File | null = null;

    let imageSrc: string = '',
        image: HTMLImageElement,
        isCropping = false,
        cropper: Cropper,
        cropperSelection: CropperSelection | null = null,
        cropperImage: CropperImage | null = null,
        cropperCanvas: CropperCanvas | null = null,
        naturalWidth = 0,
        naturalHeight = 0,
        initialImageTransform: number[] = [],
        isLowResolutionLabel: string | null = null;

    // quick check original image resolution to display a warning
    $: {
        if (cropperImage) {
            const img = new Image();
            img.onload = () => {
                isLowResolutionLabel =
                    img.naturalWidth < MIN_WIDTH ||
                    img.naturalHeight < MIN_HEIGHT
                        ? trans(
                              'form.multi_image_upload.upload_dimension_warning',
                          )
                              .replace('%width%', img.naturalWidth.toString())
                              .replace('%height%', img.naturalHeight.toString())
                        : null;
            };
            img.src = cropperImage.$image.src;
        }
    }

    onMount(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                cancel();
            }
        };
        window.addEventListener('keydown', handleKeyDown);

        // For File: create blob URL (revoked in onDestroy).
        // For CDN src: route through proxy so cropperjs canvas stays untainted.
        if (file) {
            imageSrc = URL.createObjectURL(file);
        } else if (src!.startsWith('blob:')) {
            imageSrc = src!;
        } else {
            imageSrc = `${cropProxyUrl}?url=${encodeURIComponent(src!)}`;
        }

        image = new Image();
        image.crossOrigin = 'anonymous'; // required for proxy URLs; harmless for blobs
        image.onload = () => {
            naturalWidth = image.naturalWidth;
            naturalHeight = image.naturalHeight;
        };
        image.src = imageSrc;
        cropper = new Cropper(image, {
            container: '.cropper-container',
        });
        cropperSelection = cropper.getCropperSelection();
        if (cropperSelection) cropperSelection.precise = true; // to get float in the listeners and avoid issues
        cropperImage = cropper.getCropperImage();
        cropperCanvas = cropper.getCropperCanvas();
        // customize 8 handles colors
        const cropperHandles: NodeList = document.querySelectorAll(
            'cropper-handle[action$="-resize"]',
        );
        if (cropperHandles.length > 0) {
            cropperHandles.forEach((handle) => {
                (handle as CropperHandle).themeColor = '#3643BA';
            });
        }

        cropperImage?.$ready(() => {
            if (cropperSelection && cropperCanvas && cropperImage) {
                cropperImage.$zoom(-0.05); // Small "padding" around the image to see handles better
                fitSelectionToImage();
                initialImageTransform = cropperImage.$getTransform();

                cropperImage.addEventListener('transform', () => {
                    // when zooming out, maintain selection to the image
                    requestAnimationFrame(() => {
                        if (
                            !cropperCanvas ||
                            !cropperImage ||
                            !cropperSelection
                        )
                            return;

                        const canvasRect =
                            cropperCanvas.getBoundingClientRect();
                        const imageRect = cropperImage.getBoundingClientRect();
                        const maxX = imageRect.left - canvasRect.left;
                        const maxY = imageRect.top - canvasRect.top;
                        const maxW = imageRect.width;
                        const maxH = imageRect.height;

                        const selX = parseFloat(
                            cropperSelection.getAttribute('x') ?? '0',
                        );
                        const selY = parseFloat(
                            cropperSelection.getAttribute('y') ?? '0',
                        );
                        const selW = parseFloat(
                            cropperSelection.getAttribute('width') ?? '0',
                        );
                        const selH = parseFloat(
                            cropperSelection.getAttribute('height') ?? '0',
                        );

                        const clampedW = Math.min(selW, maxW);
                        const clampedH = Math.min(selH, maxH);
                        const clampedX = Math.max(
                            maxX,
                            Math.min(selX, maxX + maxW - clampedW),
                        );
                        const clampedY = Math.max(
                            maxY,
                            Math.min(selY, maxY + maxH - clampedH),
                        );

                        if (
                            clampedX !== selX ||
                            clampedY !== selY ||
                            clampedW !== selW ||
                            clampedH !== selH
                        ) {
                            cropperSelection.$change(
                                clampedX,
                                clampedY,
                                clampedW,
                                clampedH,
                            );
                        }
                    });
                });

                cropperSelection.addEventListener('change', (event: Event) => {
                    const customEvent = event as CustomEvent;
                    if (!cropperCanvas || !cropperImage || !cropperSelection) {
                        return;
                    }
                    const selection = customEvent.detail as Selection;
                    const cropperCanvasRect =
                        cropperCanvas.getBoundingClientRect();
                    const cropperImageRect =
                        cropperImage.getBoundingClientRect();
                    const maxSelection: Selection = {
                        x: cropperImageRect.left - cropperCanvasRect.left,
                        y: cropperImageRect.top - cropperCanvasRect.top,
                        width: cropperImageRect.width,
                        height: cropperImageRect.height,
                    };
                    const inSelection =
                        selection.x >= maxSelection.x &&
                        selection.y >= maxSelection.y &&
                        selection.x + selection.width <=
                            maxSelection.x + maxSelection.width &&
                        selection.y + selection.height <=
                            maxSelection.y + maxSelection.height;

                    if (!inSelection) {
                        customEvent.preventDefault();

                        const clampedW = Math.min(
                            selection.width,
                            maxSelection.width,
                        );
                        const clampedH = Math.min(
                            selection.height,
                            maxSelection.height,
                        );
                        const clampedX = Math.max(
                            maxSelection.x,
                            Math.min(
                                selection.x,
                                maxSelection.x + maxSelection.width - clampedW,
                            ),
                        );
                        const clampedY = Math.max(
                            maxSelection.y,
                            Math.min(
                                selection.y,
                                maxSelection.y + maxSelection.height - clampedH,
                            ),
                        );

                        cropperSelection.$change(
                            clampedX,
                            clampedY,
                            clampedW,
                            clampedH,
                        );
                    }
                });
            }
        });

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    });

    onDestroy(() => {
        if (imageSrc && imageSrc.startsWith('blob:')) {
            URL.revokeObjectURL(imageSrc);
        }
    });

    // --- Confirm ---
    const confirmCrop = async () => {
        if (!imageSrc) return;
        isCropping = true;
        try {
            if (!cropper) {
                throw new Error('Cropper instance not found');
            }
            const selection = cropper.getCropperSelection();
            const displayedImage = cropper.getCropperImage();
            if (!selection || !displayedImage)
                throw new Error('Cropper elements not found');

            // Compute scale from display size to original image resolution
            const imageDisplayRect = displayedImage.getBoundingClientRect();
            const scaleX = naturalWidth / imageDisplayRect.width;
            const scaleY = naturalHeight / imageDisplayRect.height;

            // Get the selection's current display dimensions from its attributes
            const selectionWidth = parseFloat(
                selection.getAttribute('width') ?? '0',
            );
            const selectionHeight = parseFloat(
                selection.getAttribute('height') ?? '0',
            );

            const canvasElement = await selection.$toCanvas({
                width: Math.round(selectionWidth * scaleX),
                height: Math.round(selectionHeight * scaleY),
            });
            if (!canvasElement) throw new Error('Canvas element not found');

            croppedFile = await new Promise<File>((resolve, reject) => {
                canvasElement.toBlob((blob) => {
                    if (!blob) {
                        reject(new Error('Canvas toBlob returned null'));
                        return;
                    }
                    const filename = file
                        ? file.name
                        : (src?.split('/').pop()?.split('?')[0] ?? 'image.jpg');
                    resolve(
                        new File([blob], filename, {
                            type: blob.type || 'image/jpeg',
                        }),
                    );
                }, 'image/jpeg');
            });

            dispatch('cropped', { file: croppedFile });
        } catch (err) {
            console.error('Crop failed:', err);
        } finally {
            isCropping = false;
        }
    };

    const cancel = () => {
        if (imageSrc && imageSrc.startsWith('blob:')) {
            URL.revokeObjectURL(imageSrc);
        }
        dispatch('cancel');
    };

    const fitSelectionToImage = () => {
        if (!cropperCanvas || !cropperImage || !cropperSelection) return;

        const canvasRect = cropperCanvas.getBoundingClientRect();
        const imageRect = cropperImage.getBoundingClientRect();
        cropperSelection.$change(
            imageRect.left - canvasRect.left,
            imageRect.top - canvasRect.top,
            imageRect.right - imageRect.x, // image width
            imageRect.bottom - imageRect.y, // image height
        );
    };

    const reset = () => {
        cropperImage?.$setTransform(initialImageTransform);
        fitSelectionToImage();
    };
</script>

<Modal {isOpen} fullScreen={true} onClose={cancel}>
    <!-- Header slot -->
    <svelte:fragment slot="header">
        <div class="flex items-center justify-between">
            <h2 class="text-lg font-semibold">
                {trans('form.multi_image_upload.crop.title')}
            </h2>
            <button
                type="button"
                on:click={cancel}
                class="focus:ring-border-focus rounded p-xs focus:outline-none focus:ring-2"
                aria-label="Fermer"
            >
                <VpCloseIcon class="size-xl text-content-neutral" />
            </button>
        </div>
    </svelte:fragment>

    <!-- Body -->
    <div class="flex h-[calc(100vh-89px)] flex-col">
        {#if isLowResolutionLabel}
            <div
                class="m-s border border-status-warning bg-status-warning-quiet p-xs text-center text-sm text-status-warning"
            >
                {isLowResolutionLabel}
            </div>
        {/if}
        <div class="cropper-container pb-xl"></div>

        <div class="flex w-full items-center justify-center gap-2xl pb-xl">
            <div class="flex flex-[3] items-center justify-center gap-m">
                <Button
                    intent="secondary"
                    type="button"
                    on:click={() => {
                        cropperImage?.$rotate('90deg');
                    }}
                    disabled={isCropping}
                >
                    <VpArrowUturnForwardIcon class="size-m" />
                    {trans('form.multi_image_upload.crop.rotate')}
                </Button>
                <Button
                    intent="secondary"
                    type="button"
                    on:click={reset}
                    disabled={isCropping}
                >
                    <VpRefreshIcon class="size-m" />
                    {trans('form.multi_image_upload.crop.reset')}
                </Button>
            </div>
            <div class="mr-2xl flex flex-1 items-center justify-end gap-m">
                <Button
                    intent="primary"
                    type="button"
                    on:click={cancel}
                    disabled={isCropping}
                >
                    <VpCloseIcon class="size-m" />
                    {trans('action.cancel')}
                </Button>
                <Button
                    intent="primary"
                    type="button"
                    on:click={confirmCrop}
                    disabled={isCropping}
                >
                    <VpCheckIcon class="size-m" />
                    {trans('action.confirm')}
                </Button>
            </div>
        </div>
    </div>
</Modal>

<style lang="scss">
    .cropper-container {
        :global(cropper-canvas) {
            height: 70vh;
        }
    }
</style>
